/**
 * Block deprecation
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-deprecation/
 * @see https://developer.wordpress.org/news/2023/03/block-deprecation-a-tutorial/
 */

import {
  RichText,
  useBlockProps,
  useInnerBlocksProps
} from '@wordpress/block-editor';

import { filterOutWhiteSpace } from '../utils/general-utils';

const v2 = {
  supports: {
    anchor: true,
    html: false
  },
  attributes: {
    isLogoBanner: {
      default: true,
      type: 'boolean'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    baseStyle: {
      default: '',
      type: 'string'
    },
    backgroundColor: {
      default: '',
      type: 'string'
    },
    heading: {
      default: [],
      selector: '.logo-banner__heading',
      source: 'children',
      type: 'array'
    },
    headingColor: {
      default: '',
      type: 'string'
    },
    text: {
      default: [],
      selector: '.logo-banner__text',
      source: 'children',
      type: 'array'
    },
    textColor: {
      default: '',
      type: 'string'
    },
    layoutStyle: {
      default: '',
      type: 'string'
    },
    orientationStyle: {
      default: '',
      type: 'string'
    },
    splitLayoutOption: {
      default: false,
      type: 'boolean'
    },
    logo: {
      type: 'object',
      default: {}
    },
    buttonOption: {
      default: false,
      type: 'boolean'
    },
    socialOption: {
      default: false,
      type: 'boolean'
    },
    linkTargetBlank: {
      default: false,
      type: 'boolean'
    }
  },
  migrate( attributes ) {
    // Migrate old blocks to use is-style-full-bleed className
    return {
      ...attributes,
      className: 'is-style-full-bleed'
    };
  },
  save({ attributes }) {
    const {
      borderColor,
      borderStyle,
      baseStyle,
      backgroundColor,
      heading,
      headingColor,
      text,
      textColor,
      layoutStyle,
      orientationStyle,
      logo,
      buttonOption,
      socialOption
    } = attributes;

    const blockProps = useBlockProps.save({
      className: 'block__logo-banner'
    });

    const { children, ...innerBlocksProps } = useInnerBlocksProps.save(
      { className: `inner-blocks-wrapper` }
    );

    /**
     * Handle additional logic
     */
    const styledElementClasses = filterOutWhiteSpace( [ 'logo-banner-styled-element', borderStyle ] );

    const base = baseStyle ?? '';
    const isCurved = baseStyle === 'has-curve';
    const curvedBaseWithBorder = ( isCurved && borderColor ) ? 'has-curved-border' : '';

    const layout = layoutStyle ?? '';
    const orientation = orientationStyle ?? '';
    const hasNoImage = !logo ? 'has-no-image' : '';

    /**
     * Handle styles
     */
    const bgColor = backgroundColor ? { backgroundColor } : undefined;
    const bColor = borderColor ? { borderColor } : undefined;

    const hColor = headingColor ? { color: headingColor } : undefined;
    const tColor = textColor ? { color: textColor } : undefined;

    /**
     * Handle element classes
     */
    const logoBannerInnerClasses = filterOutWhiteSpace( [
      'logo-banner--inner',
      layout,
      orientation,
      base,
      curvedBaseWithBorder,
      hasNoImage
    ] );

    return (
      <div { ...blockProps }>

        <div
          className={ logoBannerInnerClasses }
          style={{ ...bgColor, color: borderColor }}
        >

          <div
            className={ styledElementClasses }
            style={{ ...bColor }}
          >
          </div>

          <div className='logo-banner-wrapper'>

            <div className="logo-element">
              <img className="logo"
                src={ logo.sizes?.full.url }
              />
            </div>

            <div className="text-side-elements" style={{ color: '#000' }}>

              <RichText.Content
                className='heading-2 logo-banner__heading mt-0 mb-25 lg:mb-32 mx-0'
                tagName='h2'
                value={ heading }
                style={{ ...hColor }}
              />

              <RichText.Content
                className='paragraph-1 logo-banner__text font-400 my-0 mx-0'
                tagName='p'
                value={ text }
                style={{ ...tColor }}
              />

              { ( buttonOption || socialOption ) && (
                <div {...innerBlocksProps}>
                  { children }
                </div>
              ) }

            </div>
          </div>
        </div>

      </div>
    );
  }
};

const v1 = {
  supports: {
    anchor: true,
    html: false
  },
  attributes: {
    isLogoBanner: {
      default: true,
      type: 'boolean'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    baseStyle: {
      default: '',
      type: 'string'
    },
    backgroundColor: {
      default: '',
      type: 'string'
    },
    heading: {
      default: [],
      selector: '.logo-banner_heading',
      source: 'children',
      type: 'array'
    },
    headingColor: {
      default: '',
      type: 'string'
    },
    text: {
      default: [],
      selector: '.logo-banner_text',
      source: 'children',
      type: 'array'
    },
    textColor: {
      default: '',
      type: 'string'
    },
    layoutStyle: {
      default: '',
      type: 'string'
    },
    orientationStyle: {
      default: '',
      type: 'string'
    },
    splitLayoutOption: {
      default: false,
      type: 'boolean'
    },
    logo: {
      type: 'object',
      default: {}
    },
    buttonOption: {
      default: false,
      type: 'boolean'
    },
    socialOption: {
      default: false,
      type: 'boolean'
    }
  },
  migrate( attributes ) {
    // Migrate old blocks to use is-style-full-bleed className
    return {
      ...attributes,
      className: 'is-style-full-bleed'
    };
  },
  save({ attributes }) {
    const {
      borderColor,
      borderStyle,
      baseStyle,
      backgroundColor,
      heading,
      headingColor,
      text,
      textColor,
      layoutStyle,
      orientationStyle,
      logo,
      buttonOption,
      socialOption
    } = attributes;

    const blockProps = useBlockProps.save({
      className: 'block__logo-banner'
    });

    const { children, ...innerBlocksProps } = useInnerBlocksProps.save(
      { className: `inner-blocks-wrapper` }
    );

    const styledElementClasses = filterOutWhiteSpace( [ 'logo-banner-styled-element', borderStyle ] );
    const base = baseStyle ?? '';
    const isCurved = baseStyle === 'has-curve';
    const curvedBaseWithBorder = ( isCurved && borderColor ) ? 'has-curved-border' : '';
    const layout = layoutStyle ?? '';
    const orientation = orientationStyle ?? '';

    const hasNoImage = !logo ? 'has-no-image' : '';

    const bgColor = backgroundColor ? { backgroundColor } : undefined;
    const bColor = borderColor ? { borderColor } : undefined;
    const hColor = headingColor ? { color: headingColor } : undefined;
    const tColor = textColor ? { color: textColor } : undefined;

    const logoBannerInnerClasses = filterOutWhiteSpace( [
      'logo-banner--inner',
      layout,
      orientation,
      base,
      curvedBaseWithBorder,
      hasNoImage
    ] );

    return (

      <div { ...blockProps }>

        <div
          className={ logoBannerInnerClasses }
          style={{ ...bgColor, color: borderColor }}
        >

          <div
            className={ styledElementClasses }
            style={{ ...bColor }}
          >
          </div>

          <div className='logo-banner-wrapper'>

            <div className='logo-element'>
              <img className='logo'
                src={ logo.sizes?.full.url }
              />
            </div>

            <div className='text-side-elements' style={{ color: '#000' }}>

              <RichText.Content
                className='heading-2 logo-banner__heading mt-0 mb-24 mx-0'
                tagName='h2'
                allowedFormats={ [ 'core/bold', 'core/italic' ] }
                value={ heading }
                style={{ ...hColor }}
              />

              <RichText.Content
                className='paragraph-1 logo-banner__text font-400 my-0 mx-0'
                tagName='p'
                value={ text }
                style={{ ...tColor }}
              />

              { ( buttonOption || socialOption ) && (
                <div {...innerBlocksProps}>
                  { children }
                </div>
              ) }

            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default [ v2, v1 ];
